import { 
  customerService as baseCustomerService,
  fleetService as baseFleetService,
  canopyService as baseCanopyService,
  appService as baseAppService,
  telemetryService as baseTelemetryService
} from './apiClient';

// Context-aware API client that automatically includes current context
export const createContextAwareApiClient = (getContextData, validateContext) => {
  
  // Helper function to add context to request data
  const addContextToData = (data = {}, requiredContext) => {
    const context = getContextData();
    const requestData = {
      ...data,
      customerId: context.customerId
    };
    // Only operations scoped to an existing fleet should receive the selected
    // fleet ID. Customer-scoped fleet creation must preserve the new fleetId
    // supplied by the form.
    if (requiredContext === 'fleet') requestData.fleetId = context.fleetId;
    return requestData;
  };

  // Helper function to validate context before making request
  const validateAndMakeRequest = async (requiredContext, baseService, method, ...args) => {
    if (!validateContext(requiredContext)) {
      const context = getContextData();
      let errorMessage = 'Context validation failed';
      
      if (requiredContext === 'customer' && !context.hasCustomer) {
        errorMessage = 'Please select a customer before performing this operation';
      } else if (requiredContext === 'fleet' && !context.hasCustomer) {
        errorMessage = 'Please select a customer before performing this operation';
      } else if (requiredContext === 'fleet' && !context.hasFleet) {
        errorMessage = 'Please select a fleet before performing this operation';
      }
      
      throw new Error(errorMessage);
    }
    
    // Add context to mutation bodies while preserving each service method's
    // argument order: create*(data), update*(id, data).
    if (method.startsWith('create')) {
      const [data, ...otherArgs] = args;
      const requestData = addContextToData(data, requiredContext);
      return baseService[method](requestData, ...otherArgs);
    }
    if (method.startsWith('update')) {
      const [id, data, ...otherArgs] = args;
      const requestData = addContextToData(data, requiredContext);
      return baseService[method](id, requestData, ...otherArgs);
    }
    
    return baseService[method](...args);
  };

  // Customer API Service (no context required)
  const customerService = {
    getCustomers: (...args) => baseCustomerService.getCustomers(...args),
    getCustomerById: (...args) => baseCustomerService.getCustomerById(...args),
    createCustomer: (...args) => baseCustomerService.createCustomer(...args),
    updateCustomer: (...args) => baseCustomerService.updateCustomer(...args),
    deleteCustomer: (...args) => baseCustomerService.deleteCustomer(...args)
  };

  // Fleet API Service (requires customer context)
  const fleetService = {
    getFleets: (...args) => validateAndMakeRequest('customer', baseFleetService, 'getFleets', ...args),
    getFleetsByCustomerId: (...args) => baseFleetService.getFleetsByCustomerId(...args),
    getFleetById: (...args) => validateAndMakeRequest('customer', baseFleetService, 'getFleetById', ...args),
    createFleet: (...args) => validateAndMakeRequest('customer', baseFleetService, 'createFleet', ...args),
    updateFleet: (...args) => validateAndMakeRequest('customer', baseFleetService, 'updateFleet', ...args),
    deleteFleet: (...args) => validateAndMakeRequest('customer', baseFleetService, 'deleteFleet', ...args)
  };

  // Canopy API Service (requires both customer and fleet context)
  const canopyService = {
    getCanopies: (...args) => validateAndMakeRequest('fleet', baseCanopyService, 'getCanopies', ...args),
    getCanopiesByFleetId: (...args) => validateAndMakeRequest('customer', baseCanopyService, 'getCanopiesByFleetId', ...args),
    getCanopyById: (...args) => validateAndMakeRequest('fleet', baseCanopyService, 'getCanopyById', ...args),
    createCanopy: (...args) => validateAndMakeRequest('fleet', baseCanopyService, 'createCanopy', ...args),
    updateCanopy: (...args) => validateAndMakeRequest('fleet', baseCanopyService, 'updateCanopy', ...args),
    deleteCanopy: (...args) => validateAndMakeRequest('fleet', baseCanopyService, 'deleteCanopy', ...args)
  };

  // App API Service (requires customer context)
  const appService = {
    getApps: (...args) => validateAndMakeRequest('customer', baseAppService, 'getApps', ...args),
    getAppById: (...args) => validateAndMakeRequest('customer', baseAppService, 'getAppById', ...args),
    getAppsByCustomerId: (...args) => baseAppService.getAppsByCustomerId(...args),
    createApp: (...args) => validateAndMakeRequest('customer', baseAppService, 'createApp', ...args),
    updateApp: (...args) => validateAndMakeRequest('customer', baseAppService, 'updateApp', ...args),
    deleteApp: (...args) => validateAndMakeRequest('customer', baseAppService, 'deleteApp', ...args)
  };

  // Telemetry API Service (requires fleet context for device-specific data)
  const telemetryService = {
    getTelemetry: (...args) => validateAndMakeRequest('fleet', baseTelemetryService, 'getTelemetry', ...args),
    getLatestTelemetry: (...args) => validateAndMakeRequest('fleet', baseTelemetryService, 'getLatestTelemetry', ...args)
  };

  return {
    customerService,
    fleetService,
    canopyService,
    appService,
    telemetryService
  };
};
